import React, { useState } from "react";
import type { Theme } from "../lib/theme";
import type { ContingencyPolicy, ContingencyStep } from "../lib/contingency";
import { CONTINGENCY_GUIDANCE } from "../lib/contingency";

/**
 * How this project carries risk, said out loud.
 *
 * The switches are the small part. The part that matters is the block
 * underneath them, which writes out this estimate's own arithmetic in the
 * order it happens, with its own numbers in it. A percentage of a percentage
 * is the one figure on an estimate that cannot be checked by reading the
 * sheet, and "5% contingency" has meant two different numbers in every
 * negotiation it has ever appeared in. Showing the sum is what makes the
 * figure defensible; describing the feature would not.
 */
export default function ContingencyPolicyPanel({
  policy, setPolicy, lineDefaultPct, steps, isLocked, T, FONT, fmt,
}: {
  policy: ContingencyPolicy;
  setPolicy: (p: ContingencyPolicy) => void;
  /** The per-line default rate, which lives on the estimate, not the policy. */
  lineDefaultPct: number;
  steps: ContingencyStep[];
  isLocked: boolean;
  T: Theme;
  FONT: string;
  fmt: (n: number) => string;
}) {
  const [showGuidance, setShowGuidance] = useState(false);
  const set = (patch: Partial<ContingencyPolicy>) => { if (!isLocked) setPolicy({ ...policy, ...patch }); };

  const usesReserve = policy.mode === "reserve" || policy.mode === "both";
  const usesLines   = policy.mode === "line"    || policy.mode === "both";

  const MODES: Array<[ContingencyPolicy["mode"], string, string]> = [
    ["line",    "Line-item",      `Each line carries its own. A line left on the default uses ${lineDefaultPct}%.`],
    ["reserve", "Project reserve", "One figure on the project. Per-line buffers are held but not counted."],
    ["both",    "Both",            "Buffers where the scope is soft, plus a reserve on top for what no line anticipated."],
  ];

  const pill = (active: boolean): React.CSSProperties => ({
    fontSize: 10, padding: "5px 12px", borderRadius: 3,
    border: `1px solid ${active ? T.borderAccent : T.borderSecondary}`,
    cursor: isLocked ? "default" : "pointer", fontFamily: FONT,
    background: active ? T.bgAccentLight : "transparent",
    color: active ? T.borderAccent : T.textWeak,
    fontWeight: active ? 600 : 400, whiteSpace: "nowrap",
  });

  const caption: React.CSSProperties = {
    fontSize: 10, color: T.textMuted, letterSpacing: ".12em",
    textTransform: "uppercase", marginBottom: 6,
  };

  return (
    <div style={{ marginTop: 18, borderTop: `1px solid ${T.borderSecondary}`, paddingTop: 14 }}>
      <div style={caption}>How contingency is carried</div>

      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {MODES.map(([m, label]) => (
          <button key={m} onClick={() => set({ mode: m })} style={pill(policy.mode === m)}>{label}</button>
        ))}
      </div>
      <div style={{ fontSize: 11, color: T.textMuted, marginTop: 7, lineHeight: 1.5 }}>
        {MODES.find(([m]) => m === policy.mode)?.[2]}
      </div>

      {usesReserve && (
        <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 16, marginTop: 14, alignItems: "start" }}>
          <div>
            <div style={caption}>Reserve %</div>
            <input className="input-field" type="number" step=".5" min="0"
              value={policy.reservePct}
              onChange={e => set({ reservePct: +e.target.value })}
              readOnly={isLocked}
              style={{ width: "100%", boxSizing: "border-box" }} />
          </div>
          <div>
            <div style={caption}>Calculated on</div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              <button onClick={() => set({ base: "direct" })} style={pill(policy.base === "direct")}>
                Direct cost
              </button>
              <button onClick={() => set({ base: "withMarkup" })} style={pill(policy.base === "withMarkup")}>
                Cost + overhead &amp; profit
              </button>
            </div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 7, lineHeight: 1.5 }}>
              {policy.base === "direct"
                ? "The conservative reading, and the usual one in public work. A reserve sized on direct cost under-covers the exposure by your markup."
                : "The figure the owner is being asked to approve. Larger than the direct-cost base by exactly the markup."}
            </div>
          </div>
        </div>
      )}

      {usesLines && (
        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 14,
          cursor: isLocked ? "default" : "pointer" }}>
          <input type="checkbox" checked={policy.feeOnLineContingency}
            disabled={isLocked}
            onChange={e => set({ feeOnLineContingency: e.target.checked })}
            style={{ marginTop: 2, accentColor: T.borderAccent }} />
          <span style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.5 }}>
            Charge overhead &amp; profit on line-item contingency
            <span style={{ display: "block", color: T.textMuted }}>
              Off by default. Under most GMP contracts the fee is fixed and contingency carries none;
              under cost-plus-a-percentage it may.
            </span>
          </span>
        </label>
      )}

      {/* ── The sum, with this project's own numbers in it ── */}
      {steps.length > 0 && (
        <div style={{ marginTop: 16, background: T.bgInput, border: `1px solid ${T.borderSecondary}`,
          borderRadius: 6, padding: "12px 14px" }}>
          <div style={{ ...caption, marginBottom: 10 }}>On this estimate</div>
          {steps.map((s, i) => {
            const isTotal = s.label === "Total contingency";
            const muted = s.amount === 0 && !isTotal;
            return (
              <div key={i} style={{
                display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 16,
                paddingTop: i === 0 ? 0 : 9, marginTop: isTotal ? 9 : 0,
                borderTop: isTotal ? `1px solid ${T.borderSecondary}` : undefined,
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: isTotal ? 700 : 600,
                    color: muted ? T.textWeak : T.textSecondary }}>{s.label}</div>
                  <div style={{ fontSize: 10, color: T.textWeak, fontVariantNumeric: "tabular-nums",
                    marginTop: 2 }}>{s.formula}</div>
                  <div style={{ fontSize: 10, color: T.textMuted, marginTop: 3, lineHeight: 1.45,
                    maxWidth: 520 }}>{s.detail}</div>
                </div>
                <div style={{ fontSize: isTotal ? 13 : 12, fontWeight: isTotal ? 700 : 500,
                  color: muted ? T.textWeak : isTotal ? T.textPrimary : T.textSecondary,
                  fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                  {muted ? "—" : fmt(Math.round(s.amount))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <button onClick={() => setShowGuidance(v => !v)}
        style={{ marginTop: 12, background: "none", border: "none", padding: 0,
          color: T.borderAccent, fontSize: 11, fontFamily: FONT, cursor: "pointer" }}>
        {showGuidance ? "Hide" : "How contingency is normally carried"}
      </button>

      {showGuidance && (
        <div style={{ marginTop: 10, display: "grid", gap: 12 }}>
          {CONTINGENCY_GUIDANCE.map(g => (
            <div key={g.kind}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: T.textSecondary }}>{g.kind}</span>
                <span style={{ fontSize: 10, color: T.textMuted, fontVariantNumeric: "tabular-nums",
                  textAlign: "right" }}>{g.typical}</span>
              </div>
              <div style={{ fontSize: 10, color: T.textMuted, lineHeight: 1.5, marginTop: 3 }}>{g.note}</div>
            </div>
          ))}
          <div style={{ fontSize: 10, color: T.textWeak, lineHeight: 1.5, fontStyle: "italic" }}>
            Ranges are the ones an estimator would recognise, not a standard. Your own history is better
            evidence than any of them.
          </div>
        </div>
      )}
    </div>
  );
}
